import { supabase } from '@/integrations/supabase/client';

/**
 * V2-10 — consent-gated "Praca i Referencje". Thin, typed wrappers over the
 * `profile_info_requests` table + SECURITY DEFINER RPCs (migration 20260706160000).
 * The table/functions aren't in the generated types yet, hence the `as never` casts
 * (house style — see NotificationsSection, viewingActions).
 */

export type ConsentStatus = 'pending' | 'approved' | 'rejected' | 'revoked';
/** Nested sub-consent for the reference DETAILS (a third party's contact info). */
export type ReferencesStatus = 'none' | 'pending' | 'approved';

export interface ProfileInfoRequest {
  id: string;
  listing_id: string;
  owner_user_id: string;
  tenant_user_id: string;
  status: ConsentStatus;
  source: 'owner_request' | 'tenant_default';
  references_status: ReferencesStatus;
  requested_at: string;
  responded_at: string | null;
  revoked_at: string | null;
}

/** The gated bundle the owner sees after approval. Income is a BOOLEAN (>= 2x rent), never an amount. */
export interface GatedTenantInfo {
  /** Occupations declared on the tenant's income sources (may be empty). */
  job_types: string[] | null;
  /** Income category keys (uop/b2b/stipend/…) — map with employmentUtils.getCategoryLabel. */
  income_categories: string[] | null;
  is_student: boolean;
  /** true = income ≥ 2× rent, false = below, null = income not declared / rent unknown. */
  income_meets_2x: boolean | null;
  has_references: boolean;
  student_university: string | null;
  student_starting_year: number | null;
  student_graduation_year: number | null;
  student_graduation_month: number | null;
}

/** A single revealed reference (only when the reference sub-consent is approved). */
export interface GatedReference {
  landlord_first_name: string;
  landlord_last_name: string | null;
  tenancy_from: string | null;
  tenancy_to: string | null;
  is_current_tenancy: boolean;
  landlord_phone: string | null;
  landlord_email: string | null;
}

/** The single consent row for a (listing, owner, tenant) triple, or null if none exists yet. */
export async function fetchConsentRow(
  listingId: string,
  ownerId: string,
  tenantUserId: string,
): Promise<ProfileInfoRequest | null> {
  const { data, error } = await supabase
    .from('profile_info_requests' as never)
    .select('*')
    .eq('listing_id', listingId)
    .eq('owner_user_id', ownerId)
    .eq('tenant_user_id', tenantUserId)
    .maybeSingle();
  if (error) throw error;
  return (data as ProfileInfoRequest | null) ?? null;
}

/** Owner asks the tenant for access. Idempotent for pending/approved; re-opens after reject/revoke. */
export async function requestProfileInfo(
  listingId: string,
  tenantUserId: string,
): Promise<ProfileInfoRequest> {
  const { data, error } = await supabase.rpc('request_profile_info' as never, {
    p_listing_id: listingId,
    p_tenant_user_id: tenantUserId,
  } as never);
  if (error) throw error;
  return data as ProfileInfoRequest;
}

/** Tenant approves (true) or rejects (false) a pending request. */
export async function respondProfileInfo(
  requestId: string,
  approve: boolean,
): Promise<ProfileInfoRequest> {
  const { data, error } = await supabase.rpc('respond_profile_info' as never, {
    p_id: requestId,
    p_approve: approve,
  } as never);
  if (error) throw error;
  return data as ProfileInfoRequest;
}

/** Tenant revokes a previously-approved consent (re-gates the owner's view). */
export async function revokeProfileInfo(requestId: string): Promise<ProfileInfoRequest> {
  const { data, error } = await supabase.rpc('revoke_profile_info' as never, {
    p_id: requestId,
  } as never);
  if (error) throw error;
  return data as ProfileInfoRequest;
}

/** Owner reads the gated bundle for a SPECIFIC tenant (the thread). Returns null when there is no
 *  approved consent for that (owner, tenant, listing) triple. */
export async function fetchGatedTenantInfo(
  listingId: string,
  tenantUserId: string,
): Promise<GatedTenantInfo | null> {
  const { data, error } = await supabase.rpc('owner_gated_tenant_info' as never, {
    p_listing_id: listingId,
    p_tenant_user_id: tenantUserId,
  } as never);
  if (error) throw error;
  const rows = data as GatedTenantInfo[] | null;
  return rows && rows.length > 0 ? rows[0] : null;
}

/** Tenant reads their OWN reference details (RLS: own rows only) — used to spell out the reference row
 *  in the "Zobacz szczegóły" pop-up once the tenant has also shared the reference details. */
export async function fetchMyReferences(userId: string): Promise<GatedReference[]> {
  const { data, error } = await supabase
    .from('landlord_references')
    .select('landlord_first_name, landlord_last_name, tenancy_from, tenancy_to, is_current_tenancy, landlord_phone, landlord_email')
    .eq('user_id', userId)
    .order('tenancy_from', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return (data as GatedReference[] | null) ?? [];
}

/** Tenant reads their OWN bundle exactly as an owner would see it for this listing (rent drives the
 *  income ≥ 2× boolean). Powers the "Jak to działa" / "Zobacz szczegóły" pop-ups on the tenant card. */
export async function fetchTenantSharedInfo(listingId: string): Promise<GatedTenantInfo | null> {
  const { data, error } = await supabase.rpc('tenant_shared_info' as never, {
    p_listing_id: listingId,
  } as never);
  if (error) throw error;
  const rows = data as GatedTenantInfo[] | null;
  return rows && rows.length > 0 ? rows[0] : null;
}

// ── Reference DETAILS sub-consent (separate request → approve; revoke is folded into revokeProfileInfo)
export async function requestReferences(listingId: string, tenantUserId: string): Promise<ProfileInfoRequest> {
  const { data, error } = await supabase.rpc('request_references' as never, {
    p_listing_id: listingId,
    p_tenant_user_id: tenantUserId,
  } as never);
  if (error) throw error;
  return data as ProfileInfoRequest;
}

export async function respondReferences(requestId: string, approve: boolean): Promise<ProfileInfoRequest> {
  const { data, error } = await supabase.rpc('respond_references' as never, {
    p_id: requestId,
    p_approve: approve,
  } as never);
  if (error) throw error;
  return data as ProfileInfoRequest;
}

/** Owner reads the reference details for a tenant — only when the reference sub-consent is approved. */
export async function fetchGatedReferences(listingId: string, tenantUserId: string): Promise<GatedReference[]> {
  const { data, error } = await supabase.rpc('owner_gated_references' as never, {
    p_listing_id: listingId,
    p_tenant_user_id: tenantUserId,
  } as never);
  if (error) throw error;
  return (data as GatedReference[] | null) ?? [];
}

// ── Master "share by default" toggle (tenant_profiles.share_package_by_default) ──────────────────
// Reads via get_my_tenant_private() (20260924090036): the base table's SELECT grant
// to authenticated no longer covers this column (Conv 30/4 — it's redacted from
// tenant_profiles_public, so it must not be role-wide readable). The RPC is
// self-scoped on auth.uid(), so `userId` is kept only for API-signature/call-site
// stability with the write side below — every caller already passes their own id.
export async function fetchShareByDefault(_userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('get_my_tenant_private' as never);
  if (error) throw error;
  const row = (data as { share_package_by_default?: boolean }[] | null)?.[0];
  return Boolean(row?.share_package_by_default);
}

export async function setShareByDefault(userId: string, on: boolean): Promise<void> {
  const { error } = await supabase
    .from('tenant_profiles')
    .update({ share_package_by_default: on } as never)
    .eq('user_id', userId);
  if (error) throw error;
}

/** "Kto ma dostęp" — the owners currently holding this tenant's package (approved, not revoked). */
export interface AccessEntry extends ProfileInfoRequest {
  owner_name: string | null;
}

export async function fetchAccessList(tenantUserId: string): Promise<AccessEntry[]> {
  const { data, error } = await supabase
    .from('profile_info_requests' as never)
    .select('*')
    .eq('tenant_user_id', tenantUserId)
    .eq('status', 'approved')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  const rows = (data as ProfileInfoRequest[] | null) ?? [];
  if (rows.length === 0) return [];

  // Enrich with the owner's name (best-effort single lookup).
  const ownerIds = [...new Set(rows.map((r) => r.owner_user_id))];
  const { data: owners } = await supabase.from('users').select('id, first_name, last_name').in('id', ownerIds);
  const ownerMap = new Map((owners as { id: string; first_name: string | null; last_name: string | null }[] | null ?? [])
    .map((o) => [o.id, [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || null]));
  return rows.map((r) => ({ ...r, owner_name: ownerMap.get(r.owner_user_id) ?? null }));
}
